'use client';

import { ReactNode, useState } from 'react';
import {
  FormControl,
  FormControlProps as ChakraFormControlProps,
  FormLabel,
  FormLabelProps,
  Icon,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { MdError } from 'react-icons/md';

type FormControlProps = Omit<ChakraFormControlProps, 'label'>;

interface Props extends FormControlProps {
  hasValue: boolean;
  label?: ReactNode;
  labelProps?: FormLabelProps;
  error?: string;
  inputLeft?: ReactNode;
  inputRight?: ReactNode;
  size?: InputProps['size'];
  children?: ReactNode;
  maxW?: InputProps['maxW'];
}

export type InputWrapperProps = Props;

export const InputWrapper = (props: Props) => {
  const {
    error,
    hasValue,
    label,
    labelProps,
    inputLeft,
    inputRight,
    size,
    maxW,
    children,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);

  const { isOpen, onToggle, onOpen, onClose } = useDisclosure();

  const hasError = Boolean(error);
  const hasLabel = Boolean(label);
  const hasRightElement = Boolean(inputRight);
  const hasLeftElement = Boolean(inputLeft);

  const getLabelProps = (): FormLabelProps => {
    if (!hasLabel || (!isFocused && !hasValue)) return {};

    return {
      transform: 'scale(0.85) translateY(-2rem)',
    };
  };

  return (
    <FormControl
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      maxW={maxW}
      {...rest}
    >
      {hasLabel && (
        <FormLabel
          top={0}
          left={0}
          position="absolute"
          backgroundColor="transparent"
          pointerEvents="none"
          px="0.25rem"
          my="0.5rem"
          transformOrigin="left top"
          {...labelProps}
          {...getLabelProps()}
        >
          {label}
        </FormLabel>
      )}

      <InputGroup size={size}>
        {hasLeftElement && <InputLeftElement>{inputLeft}</InputLeftElement>}

        {children}

        {(hasRightElement || hasError) && (
          <InputRightElement w="auto" minW="3rem">
            {hasError && (
              <Tooltip
                hasArrow
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                label={error}
                bg="red.500"
                color="white"
              >
                <IconButton
                  aria-label="toggle tooltip error"
                  onClick={onToggle}
                  variant="ghost"
                  size={size}
                  _hover={{ bg: 'transparent' }}
                >
                  <Icon as={MdError} color="red.500" fontSize="1.25rem" />
                </IconButton>
              </Tooltip>
            )}

            {hasRightElement && inputRight}
          </InputRightElement>
        )}
      </InputGroup>
    </FormControl>
  );
};
